import { Loading } from '@liveauctioneers/hammer-ui-core/loading';
import { SearchInput } from '@liveauctioneers/hammer-ui-core/searchInput';
import { OptionType } from '@liveauctioneers/hammer-ui-core/select';
import { cn } from '@liveauctioneers/hammer-ui-theme/cn';
import React, { useCallback, useState } from 'react';
import isEqual from 'react-fast-compare';

import { ModifyQueryParamsAction, useModifyQueryParams } from '@/hooks/queryParams/useModifyQueryParams';

import SortPageSizeFilter from './SortPageSizeFilter';
import { PaginationFilter } from './types/PaginationFilter';

type OnSearchChange = (filters?: { keyword: string; page: number; preventScrollReset: boolean }) => void;

export type SearchSortPageSizeFilterProps = {
    readonly className?: string;
    /** Whether the search is loading */
    readonly loading?: boolean;
    readonly onChange: Function;
    /** SelectOptions of what should be rendered for the page size options */
    readonly pageSizeOptions?: OptionType[];
    /** A PaginationFilter object  */
    readonly paginationFilter: PaginationFilter;
    /** Used to display result count, or any text you want really  */
    readonly resultsDisplay?: any;
    /** Text to display in the search bar when nothing is entered */
    readonly searchPlaceholder?: string;
    /** Should modify query params */
    readonly shouldModifyQueryParams?: boolean;
    /** SelectOptions of what should be rendered for the sort options */
    readonly sortOptions: OptionType[];
    readonly testid?: string;
};

export const SearchSortPageSizeFilter = ({
    className,
    loading,
    onChange,
    pageSizeOptions,
    paginationFilter,
    resultsDisplay,
    searchPlaceholder,
    shouldModifyQueryParams,
    sortOptions,
    testid = 'searchSortPageSizeFilter',
}: SearchSortPageSizeFilterProps) => {
    const modifyQueryParams = useModifyQueryParams();

    const [search, setSearch] = useState(paginationFilter.keyword);

    const onSearchChange: OnSearchChange = useCallback(
        (filters) => {
            if (shouldModifyQueryParams) {
                if (filters?.keyword === '') {
                    modifyQueryParams({
                        action: ModifyQueryParamsAction.REMOVE,
                        params: 'keyword',
                    });
                } else {
                    modifyQueryParams({
                        action: ModifyQueryParamsAction.ADD,
                        query: { keyword: filters?.keyword },
                    });
                }
            }

            onChange(filters);
        },
        [shouldModifyQueryParams, onChange, modifyQueryParams]
    );

    const handleSearchClear = useCallback(() => {
        setSearch('');
        onSearchChange({ keyword: '', page: 1, preventScrollReset: true });
    }, [onSearchChange]);

    const handleSearchSubmit = useCallback(() => {
        onSearchChange({ keyword: search, page: 1, preventScrollReset: true });
    }, [onSearchChange, search]);

    return (
        <div
            className={cn('border-b border-border-secondary', className)}
            data-testid={testid}
        >
            <div className="flex justify-between gap-sm xsMax:flex-col xsMax:gap-0">
                <div className="flex gap-sm xsMax:w-full">
                    <SearchInput
                        aria-label="Search Filter"
                        button-aria-label="Search Button"
                        className="mb-lg xsMax:mb-sm xsMax:w-full"
                        data-testid="search-filter"
                        onClear={handleSearchClear}
                        onSubmit={handleSearchSubmit}
                        onTextChange={setSearch}
                        placeholder={searchPlaceholder}
                        small
                        value={search}
                    />
                    {loading && <Loading size={32} />}
                </div>
                <SortPageSizeFilter
                    onChange={onChange}
                    pageSizeOptions={pageSizeOptions}
                    paginationFilter={paginationFilter}
                    shouldModifyQueryParams={shouldModifyQueryParams}
                    sortOptions={sortOptions}
                />
            </div>
            {resultsDisplay}
        </div>
    );
};

export default React.memo<SearchSortPageSizeFilterProps>(SearchSortPageSizeFilter, isEqual);
